import * as THREE from 'three';
import {RationalBezierCurveEvaluator2D} from './RationalBezierCurveEvaluator2D';
import {RationalBSplineCurveEvaluator2D} from './RationalBSplineCurveEvaluator2D';

/**
 * Puppy CAD 内核 `Geom2dAdaptor_Curve` 的样条子集等价适配器。
 *
 * 职责：直接在 Bezier/B-Spline 原生参数域提供 D0/D1/D2、C1 连续区间和 Puppy CAD 内核 采样数量。
 * 本类不会把 B-Spline 转换为 Bezier spans，确保主求交路径与 Puppy CAD 内核 一致。
 */
export class ParametricSplineCurveAdaptor2D {
    /** Puppy CAD 内核 参数混淆容差。 */
    private static readonly PARAMETER_CONFUSION = 1e-10;

    /** Puppy CAD 内核 采样数量上限。 */
    private static readonly MAXIMUM_SAMPLE_COUNT = 300;

    /** 参数曲线定义。 */
    private readonly curve: LineSplineCurveDefinition2D;

    /** 有理 Bezier 求值器。 */
    private readonly bezierEvaluator: RationalBezierCurveEvaluator2D;

    /** 有理 B-Spline 求值器。 */
    private readonly bsplineEvaluator: RationalBSplineCurveEvaluator2D;

    /**
     * 创建 Puppy CAD 内核 等价参数曲线适配器。
     * @param curve Bezier 或 B-Spline 定义。
     * @param bezierEvaluator 有理 Bezier 求值器。
     * @param bsplineEvaluator 有理 B-Spline 求值器。
     */
    public constructor(
        curve: LineSplineCurveDefinition2D,
        bezierEvaluator: RationalBezierCurveEvaluator2D = new RationalBezierCurveEvaluator2D(),
        bsplineEvaluator: RationalBSplineCurveEvaluator2D = new RationalBSplineCurveEvaluator2D(),
    ) {
        this.curve = curve;
        this.bezierEvaluator = bezierEvaluator;
        this.bsplineEvaluator = bsplineEvaluator;
    }

    /** 获取曲线原生参数下界。 */
    public get firstParameter(): number {
        return this.curve.firstParameter ?? 0;
    }

    /** 获取曲线原生参数上界。 */
    public get lastParameter(): number {
        return this.curve.lastParameter ?? 1;
    }

    /** 获取曲线是否周期。 */
    public get periodic(): boolean {
        return this.curve.kind === 'bspline' && this.curve.periodic;
    }

    /** 获取周期曲线周期。 */
    public get period(): number {
        return this.lastParameter - this.firstParameter;
    }

    /**
     * 校验参数曲线定义。
     * @returns 定义可供 Puppy CAD 内核 等价求交器使用时返回 true。
     */
    public isValid(): boolean {
        /**
         * 使用局部常量保持判别联合在数组回调中的收窄结果。
         * 直接从 `this.curve` 读取会让 TypeScript 在 `every()` 回调中重新放宽为联合类型，
         * 从而错误地认为 Bezier 分支也可能访问仅属于 B-Spline 的节点向量。
         */
        const curve = this.curve;
        const weights = this.resolveWeights();
        if (
            curve.controlPoints.length < 2
            || !Number.isFinite(this.firstParameter)
            || !Number.isFinite(this.lastParameter)
            || this.lastParameter <= this.firstParameter
            || !curve.controlPoints.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
            || weights.length !== curve.controlPoints.length
            || !weights.every((weight) => Number.isFinite(weight) && weight > 0)
        ) {
            return false;
        }
        if (curve.kind === 'bezier') return true;

        // B-Spline 分支完成判别后再缓存节点向量，确保后续回调只依赖 B-Spline 专属数据。
        const knots = curve.knots;
        return Number.isInteger(curve.degree)
            && curve.degree >= 1
            && curve.degree < curve.controlPoints.length
            && knots.length === curve.controlPoints.length + curve.degree + 1
            && knots.every(Number.isFinite)
            && knots.every((knot, index) => index === 0 || knot >= knots[index - 1]!);
    }

    /**
     * 计算曲线点。
     * @param parameter 原生参数。
     * @returns 二维曲线点。
     */
    public value(parameter: number): THREE.Vector2 {
        if (this.curve.kind === 'bezier') {
            return this.bezierEvaluator.evaluatePoint(
                this.curve.controlPoints,
                this.resolveWeights(),
                this.toBezierLocalParameter(parameter),
            );
        }
        return this.bsplineEvaluator.evaluatePoint(this.curve, parameter);
    }

    /**
     * 计算曲线一阶导数。
     * @param parameter 原生参数。
     * @returns 对原生参数的一阶导数。
     */
    public derivative(parameter: number): THREE.Vector2 {
        if (this.curve.kind === 'bezier') {
            return this.bezierEvaluator.evaluateDerivative(
                this.curve.controlPoints,
                this.resolveWeights(),
                this.toBezierLocalParameter(parameter),
            ).divideScalar(this.period);
        }
        return this.bsplineEvaluator.evaluateDerivative(this.curve, parameter);
    }

    /**
     * 计算曲线二阶导数。
     * @param parameter 原生参数。
     * @returns 对原生参数的二阶导数。
     */
    public secondDerivative(parameter: number): THREE.Vector2 {
        if (this.curve.kind === 'bezier') {
            return this.bezierEvaluator.evaluateSecondDerivative(
                this.curve.controlPoints,
                this.resolveWeights(),
                this.toBezierLocalParameter(parameter),
            ).divideScalar(this.period * this.period);
        }
        return this.bsplineEvaluator.evaluateSecondDerivative(this.curve, parameter);
    }

    /**
     * 创建完整参数曲线有界 domain。
     * @param endpointTolerance 两端世界空间容差。
     * @returns Puppy CAD 内核 等价参数域。
     */
    public createDomain(endpointTolerance: number): ParametricIntersectionDomain2D {
        return {
            hasFirstPoint: true,
            hasLastPoint: true,
            firstPoint: this.value(this.firstParameter),
            lastPoint: this.value(this.lastParameter),
            firstParameter: this.firstParameter,
            lastParameter: this.lastParameter,
            firstTolerance: endpointTolerance,
            lastTolerance: endpointTolerance,
            closed: this.periodic,
            equivalentFirstParameter: this.periodic ? this.firstParameter : null,
            equivalentLastParameter: this.periodic ? this.lastParameter : null,
        };
    }

    /**
     * 解析 Puppy CAD 内核 `GeomAbs_C1` 复合区间。
     *
     * B-Spline 只在连续性 `degree-multiplicity < 1` 的内部 knot 分段；普通 C1/C2 knot 不拆。
     * @returns 含全域首尾的参数边界。
     */
    public resolveC1IntervalBoundaries(): number[] {
        if (this.curve.kind === 'bezier') {
            return [this.firstParameter, this.lastParameter];
        }
        const boundaries = [this.firstParameter];
        let index = 0;
        while (index < this.curve.knots.length) {
            const knot = this.curve.knots[index]!;
            let nextIndex = index + 1;
            while (
                nextIndex < this.curve.knots.length
                && Math.abs(this.curve.knots[nextIndex]! - knot) <= ParametricSplineCurveAdaptor2D.PARAMETER_CONFUSION
            ) {
                nextIndex += 1;
            }
            const multiplicity = nextIndex - index;
            if (
                knot > this.firstParameter + ParametricSplineCurveAdaptor2D.PARAMETER_CONFUSION
                && knot < this.lastParameter - ParametricSplineCurveAdaptor2D.PARAMETER_CONFUSION
                && this.curve.degree - multiplicity < 1
            ) {
                boundaries.push(knot);
            }
            index = nextIndex;
        }
        boundaries.push(this.lastParameter);
        return boundaries;
    }

    /**
     * 计算 Puppy CAD 内核 `Geom2dInt_Geom2dCurveTool::NbSamples` 等价采样数量。
     * @param first 子域起始参数。
     * @param last 子域终止参数。
     * @returns 包含首尾点的均匀采样数量。
     */
    public resolveSampleCount(first: number, last: number): number {
        if (this.curve.kind === 'bezier') {
            return Math.min(
                ParametricSplineCurveAdaptor2D.MAXIMUM_SAMPLE_COUNT,
                3 + this.curve.controlPoints.length,
            );
        }
        const distinctKnotCount = this.countDistinctKnots(this.curve.knots);
        const fullLength = this.lastParameter - this.firstParameter;
        let sampleCount = distinctKnotCount * this.curve.degree;
        if (fullLength > ParametricSplineCurveAdaptor2D.PARAMETER_CONFUSION) {
            sampleCount = Math.trunc(Math.abs(last - first) / fullLength * sampleCount);
        }
        sampleCount = Math.max(sampleCount, this.curve.degree + 1, 4);
        return Math.min(sampleCount, ParametricSplineCurveAdaptor2D.MAXIMUM_SAMPLE_COUNT);
    }

    /**
     * 将原生参数映射为 Bezier 局部参数。
     * @param parameter 原生参数。
     * @returns `[0,1]` 局部参数。
     */
    private toBezierLocalParameter(parameter: number): number {
        return THREE.MathUtils.clamp((parameter - this.firstParameter) / this.period, 0, 1);
    }

    /**
     * 读取并补齐权重。
     * @returns 与控制点一一对应的权重。
     */
    private resolveWeights(): number[] {
        return !this.curve.weights || this.curve.weights.length === 0
            ? new Array<number>(this.curve.controlPoints.length).fill(1)
            : [...this.curve.weights];
    }

    /**
     * 统计展开节点向量中的不同 knot 数量。
     * @param knots 展开节点向量。
     * @returns 不同 knot 数量。
     */
    private countDistinctKnots(knots: readonly number[]): number {
        return knots.reduce((count, knot, index) => (
            index === 0 || Math.abs(knot - knots[index - 1]!) > ParametricSplineCurveAdaptor2D.PARAMETER_CONFUSION
                ? count + 1
                : count
        ), 0);
    }
}
